import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';

import type {
	useLicensedProductsQueryForGlobalAppShortcutsQuery$data,
	useLicensedProductsQueryForGlobalAppShortcutsQuery$variables,
} from './__generated__/useLicensedProductsQueryForGlobalAppShortcutsQuery.graphql.ts';

const useLicensedProductsQueryForGlobalAppShortcuts = graphql`
	query useLicensedProductsQueryForGlobalAppShortcutsQuery {
		confluence_tenantContext {
			licensedProducts {
				licenseStatus
				productKey
			}
		}
	}
`;

type Licenses = {
	hasJira: boolean;
	hasLoom: boolean;
	hasJpd: boolean;
};

type Result = Licenses & {
	licensesLoading: boolean;
};

export const useLicensedProducts = (): Result => {
	const queryResult = useQueryNoFurtherUpdateInterop<
		useLicensedProductsQueryForGlobalAppShortcutsQuery$data,
		useLicensedProductsQueryForGlobalAppShortcutsQuery$variables
	>(useLicensedProductsQueryForGlobalAppShortcuts);

	const { data, loading } = queryResult;

	return useMemo(() => {
		const licensedProducts = data?.confluence_tenantContext?.licensedProducts || [];
		const licenses = licensedProducts.reduce<Licenses>(
			(acc, product) => {
				if (product.licenseStatus !== 'ACTIVE') {
					return acc;
				}

				switch (product.productKey) {
					case 'jira-software':
						acc.hasJira = true;
						break;
					case 'loom':
						acc.hasLoom = true;
						break;
					case 'jira-product-discovery':
						acc.hasJpd = true;
						break;
					default:
						break;
				}
				return acc;
			},
			{ hasJira: false, hasLoom: false, hasJpd: false },
		);

		return {
			...licenses,
			licensesLoading: loading,
		};
	}, [data, loading]);
};
